<!--
  One audience's dashboard card: who it is, and a line per kind of unfinished work on it.

  The lines are GapItems, so they render through the shared NextStepList — same contract, same severity chips
  and the same "link steps open in a new tab" rule the audience gap audit already uses. Nothing fires from
  here: every button navigates to whichever screen owns the work.

  An audience with nothing left still gets a card. "All clear" is a result worth reading, and a card that
  vanished on completion would leave you unsure whether it was done or just never planned.
-->
<template>
  <v-card
    outlined
    class="fill-height d-flex flex-column"
  >
    <v-card-title class="text-subtitle-1 align-start">
      <a
        class="audience-link"
        @click="open()"
      >{{ audience.name }}</a>
      <v-spacer />
      <!-- Runway, said on its own chip rather than in the count. A plan can be short of road with nothing
           else outstanding (green "All clear" would be exactly the wrong read) and equally with plenty
           outstanding, where it would otherwise disappear behind the count. The line in the list below only
           appears once the redraft is the next thing worth doing; this goes amber a month out, matching the
           chip on the audiences list. -->
      <v-chip
        v-if="planHealth"
        x-small
        class="ml-2 flex-shrink-0"
        :color="planHealth.color"
        text-color="white"
        :title="healthTitle"
      >
        {{ planHealth.label }}
      </v-chip>
      <v-chip
        v-if="audience.outstanding"
        x-small
        class="ml-2 flex-shrink-0"
        :color="severest"
        text-color="white"
      >
        {{ audience.outstanding }}
      </v-chip>
      <!-- "All clear" is a claim about the whole audience, so a plan running out of road disqualifies it
           even when every line is answered. -->
      <v-chip
        v-else-if="!planHealth"
        x-small
        class="ml-2 flex-shrink-0"
        color="success"
        text-color="white"
      >
        All clear
      </v-chip>
    </v-card-title>

    <v-card-subtitle class="text-caption pb-2">
      {{ audience.typeName }}
      <span v-if="planStatus"> · {{ planStatus.label }} plan</span>
      <!-- Only when the chip is amber and the list below is silent about it — inside the line's own window
           the date is already in the line, and saying it twice on one card reads as two problems. -->
      <span v-if="runwayNote"> · {{ runwayNote }}</span>
    </v-card-subtitle>

    <v-card-text class="flex-grow-1">
      <NextStepList
        :steps="audience.items || []"
        empty-text="Nothing outstanding — plan drafted, suggestions answered, campaigns approved."
      />
    </v-card-text>
  </v-card>
</template>

<script>
import NextStepList from '@/components/common/NextStepList';

export default {
  name: 'AudienceCard',
  components: { NextStepList },
  props: {
    /**
     * An AudienceWork: { targetType, targetId, name, typeName, planStatus, planHealth,
     * lastBookedSendDate, url, outstanding, items[] }.
     */
    audience: {
      type: Object,
      required: true
    }
  },
  computed: {
    planStatus() {
      return this.$enums.AUDIENCE.PLAN_STATUS[this.audience.planStatus];
    },
    // Undefined while the plan has runway — PLAN_HEALTH only has entries for the two states worth saying.
    planHealth() {
      return this.$enums.AUDIENCE.PLAN_HEALTH[this.audience.planHealth];
    },
    lastBookedSend() {
      return this.audience.lastBookedSendDate
        ? this.$options.filters.normalDate(this.audience.lastBookedSendDate)
        : '';
    },
    healthTitle() {
      return this.lastBookedSend
        ? `Last booked send ${this.lastBookedSend}, with nothing behind it`
        : 'Nothing booked behind the last planned send';
    },
    // The dashboard's own runway line holds off until the redraft is imminent (RunwayWarningDays), so the
    // chip can be amber with nothing in the list explaining it. This is that explanation, and it stands down
    // as soon as the line takes over.
    runwayNote() {
      if (!this.planHealth || !this.lastBookedSend) return '';
      if ((this.audience.items || []).some((i) => i.group === 'runway')) return '';
      return `last planned send ${this.lastBookedSend}`;
    },
    // The count chip takes the colour of the worst thing on the card, so a wall of cards can be triaged
    // without reading a single line.
    severest() {
      const worst = Math.max(0, ...(this.audience.items || []).map((i) => i.severity || 0));
      const severity = this.$enums.AUDIENCE.GAP_SEVERITY[worst];
      return severity ? severity.color : 'grey';
    }
  },
  methods: {
    // New tab, matching NextStepList: you're working a wall of cards and shouldn't lose your place in it.
    open() {
      const href = this.$router.resolve(this.audience.url).href;
      window.open(href, '_blank', 'noopener');
    }
  }
};
</script>

<style scoped>
.audience-link {
  cursor: pointer;
  color: inherit;
  word-break: break-word;
}
.audience-link:hover {
  text-decoration: underline;
}
</style>
